import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { combineLatest, Observable, timer } from "rxjs";
import { debounce, filter, first, map, switchMap } from "rxjs/operators";
import {
  AbstractUserService,
  EntityId,
  IPerson,
  Login,
  Step
} from "src/app/interfaces";
import { LogClass, LogMethod } from "src/app/logger";
import { NavigationService } from "src/app/services";
import { PersonFacade } from "src/app/services/person.facade";
import { SubSink } from "subsink";
import { StepFacade } from "../../services/step.facade";
import { ForwardByOwnerComponent } from "./forwardByOwner.component";

@Component({
    selector: "circ-forward-by-owner-ctn",
    templateUrl: "./forwardByOwner.component.html",
    styleUrls: ["./forwardByOwner.component.scss"],
    standalone: false
})
@LogClass({ source: "ForwardByOwnerContainerComponent" })
export class ForwardByOwnerContainerComponent
  extends ForwardByOwnerComponent
  implements OnInit, OnDestroy
{
  private readonly circulationIdIndex = 0;
  private readonly stepIdIndex = 1;

  private subscriptions = new SubSink();
  public step: Step;

  constructor(
    private stepFacade: StepFacade,
    private navigate: NavigationService,
    private route: ActivatedRoute,
    private personFacade: PersonFacade,
    userService: AbstractUserService
  ) {
    super(userService);
  }
  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  @LogMethod()
  public ngOnInit(): void {
    this.subscribeSaveClick();
    this.subscribeAbortClick();

    const step$ = this.stepFacade.getCurrentStep();
    this.goToErrorPageIfEmpty(step$);
    this.setStep(step$);
    const people$ = this.getPeople(step$);
    this.setPeople(people$);
  }

  private setPeople(people$: Observable<IPerson[]>): void {
    this.subscriptions.sink = people$.subscribe((p) => (this.people = p));
  }
  private getPeople(step$: Observable<Step>): Observable<IPerson[]> {
    return step$.pipe(
      this.logger.observableOperator("People"),
      filter((s) => !!s),
      map((step) => step.props.tasks.assignees.filter((a) => !a.result)),
      this.logger.observableOperator("People-Assignees"),
      switchMap((assignees) =>
        combineLatest(assignees.map((a) => this.getPerson(a.user.login)))
      ),
      this.logger.observableOperator("People2")
    );
  }

  private getPerson(login: Login): Observable<IPerson> {
    return this.personFacade.getPersonByLogin(login as EntityId);
  }

  private setStep(step$: Observable<Step>) {
    this.subscriptions.sink = step$
      .pipe(filter((s) => !!s))
      .subscribe((s) => (this.step = s));
  }
  private goToErrorPageIfEmpty(step$: Observable<Step>) {
    this.subscriptions.sink = step$
      .pipe(
        debounce(() => timer(5000)),
        filter((s) => !s)
      )
      .subscribe(() => {
        this.logger.error(`No step found`);
        this.navigate.toError(`Step not found`);
      });
  }

  private subscribeSaveClick() {
    this.subscriptions.sink = this.saveClick
      .pipe(
        switchMap((x) => {
          return this.stepFacade.overrideForward({
            ...x,
            step: this.step.props
          });
        }),
        first()
      )
      .subscribe(
        () => {
          this.logger.info("saved.");
          this.navigate.toCirculation(this.step.props.circulationId);
        },
        (err) => {
          this.logger.error("save", err);
          this.navigate.toError(err);
        }
      );
  }

  private subscribeAbortClick() {
    this.subscriptions.sink = this.abortClick.subscribe(() => {
      this.logger.info("abort");
      this.navigate.back();
    });
  }
}
